import { GenericObject } from "src/api/flowTypes";
import { DataRetention } from "src/connections/types";

export type CustomConnectionAuthMethod =
  | "api_key"
  | "oauth2"
  | "google_oauth2"
  | "no_auth"
  | "basic"
  | "aws_signature"
  | "custom_code"
  | "hmac";

export type HMACHashAlgorithm = "sha1" | "sha256" | "sha512";
export type HMACSignatureEncoding = "hex" | "base64";
export type HMACSignatureLocation = "header" | "query_param";

export type MySQLConnectionAuthMethod = "username_password";
export type PostgreSQLConnectionAuthMethod = "username_password";
export type BigQuerySQLConnectionAuthMethod = "service_account";
export type SnowflakeConnectionAuthMethod = "basic_auth" | "key_pair";
export type RedshiftConnectionAuthMethod = "username_password" | "aws_iam";
export type MongoDBConnectionAuthMethod = "username_password";

export const BigQuerySQLConnectionDefaultAuthScope =
  "https://www.googleapis.com/auth/bigquery";

export const BigQuerySQLConnectionAuthScopes = [
  BigQuerySQLConnectionDefaultAuthScope,
  "https://www.googleapis.com/auth/bigquery.insertdata",
  "https://www.googleapis.com/auth/cloud-platform",
  "https://www.googleapis.com/auth/cloud-platform.read-only",
  "https://www.googleapis.com/auth/devstorage.read_only",
  "https://www.googleapis.com/auth/devstorage.read_write",
  "https://www.googleapis.com/auth/devstorage.full_control",
  "https://www.googleapis.com/auth/drive.readonly",
] as const;

export type BigQuerySQLConnectionAuthScope =
  (typeof BigQuerySQLConnectionAuthScopes)[number];

type CustomConnectionConfigurationCreate = {
  base_url?: string;
  probe_url?: string | null;
  media_key?: string | null;
  headers?: Record<string, string>;
  auth_settings?: ConnectionCreateAuthConfig;
};

type T1ConnectionConfigurationCreate = object;

type RetoolConnectionConfigurationCreate = {
  base_url?: string;
};

type InboundWebhookConnectionConfigurationCreate = {
  media_key?: string | null;
  webhook_auth_settings?: InboundWebhookConnectionCreateAuthConfig;
  custom_success_status_code?: number | null;
};
type SSHTunnelConfig = {
  host: string;
  port: number;
  user: string;
};
export type TLSMode = "require" | "verify-ca" | "verify-full";
type MutualTLSConfig = {
  mode: TLSMode;
  client_certificate: string;
  ca_certificate: string;
};

type CockroachDBRetryConfig = {
  max_retries: number;
  max_backoff: number | undefined;
};

type DBConnectionConfigurationCreate = {
  auth_method?:
    | PostgreSQLConnectionAuthMethod
    | BigQuerySQLConnectionAuthMethod
    | SnowflakeConnectionAuthMethod
    | RedshiftConnectionAuthMethod
    | MongoDBConnectionAuthMethod;

  // ssh tunnel
  has_ssh_tunnel_enabled?: boolean;
  ssh_tunnel?: SSHTunnelConfig;

  // postgres
  database_name?: string;
  host?: string;
  port?: number | null;
  has_mtls_enabled?: boolean;
  mutual_tls?: MutualTLSConfig;

  // cockroachdb
  has_retry_enabled?: boolean;
  retry_config?: CockroachDBRetryConfig;

  // bigquery
  scopes?: string[];

  // snowflake
  account_identifier?: string;
  warehouse_name?: string;
  database_schema?: string | null;
  user_role?: string | null;

  // redshift
  cluster_identifier?: string;
  aws_region?: string;
  role_arn?: string;
  ssl_mode?: RedshiftSSLMode;
  has_ssl_enabled?: boolean;

  // mongodb
  auth_source?: string;
  non_srv_host_string?: string;
};

export type ConnectionConfigurationCreate = {
  allow_test_invocations?: boolean;
} & CustomConnectionConfigurationCreate &
  T1ConnectionConfigurationCreate &
  InboundWebhookConnectionConfigurationCreate &
  DBConnectionConfigurationCreate &
  RetoolConnectionConfigurationCreate;

export type ConnectionCreateAuthConfig =
  | ConnectionCreateAuthBase
  | ConnectionCreateAuthAPIKey
  | ConnectionCreateAuthOAuth2
  | ConnectionCreateAuthAWSSignature;

// No Auth & Basic Auth
export type ConnectionCreateAuthBase = {
  auth_method: CustomConnectionAuthMethod;
  ssl_enabled: boolean;
};

export type ConnectionCreateAuthAPIKey = {
  header: string;
  prefix: string;
} & ConnectionCreateAuthBase;

export type ConnectionCreate = {
  name: string;
  provider: Provider | string;
  secrets?: {
    key: string;
    value: string | null;
    environment?: string | null;
  }[];
  resource_configs?: ResourceConfig[];
  is_sandbox: boolean;
  configuration: ConnectionConfigurationCreate;
  enable_non_prod_configs: boolean;
  non_prod_env_configs: Record<string, ConnectionConfigurationCreate>;
  data_retention?: DataRetention;
  has_raw_response_enabled?: boolean;
  has_raw_requests_enabled_in_resource?: boolean;
  disable_credential_bg_jobs?: boolean;
};

export type ConnectionCreateAuthOAuth2 = {
  auth_method: CustomConnectionAuthMethod;
  header?: string;
  prefix?: string;
  grant_type?: string;
  uses_basic_auth: boolean;
  basic_auth_header?: string;
  basic_auth_prefix?: string;
  authorize_url: string;
  authorize_query_params?: { [key: string]: string };
  authorize_headers?: { [key: string]: string };
  authorize_body?: object;
  refresh_url: string;
  refresh_query_params?: { [key: string]: string };
  refresh_headers?: { [key: string]: string };
  refresh_body?: object;
  scopes?: Array<string>;
  scope_delimiter?: string;
  token_refresh_interval?: number;
} & ConnectionCreateAuthBase;

export type ConnectionCreateAuthAWSSignature = {
  auth_method: CustomConnectionAuthMethod;
  aws_access_key: string;
  aws_host: string;
  aws_region: string;
  aws_service: string;
} & ConnectionCreateAuthBase;

export type InboundWebhookConnectionAuthMethod =
  | "no_auth"
  | "basic"
  | "api_key"
  | "hmac"
  | "hmac_persona"
  | "hmac_arva"
  | "hmac_syntage"
  | "hmac_trulioo_handshake";
export type InboundWebhookHMACHashAlgorithm = "sha1" | "sha256" | "sha512";
export type InboundWebhookHMACSignatureEncoding = "hex" | "base64";

export type InboundWebhookConnectionCreateConfig = {
  webhook_auth_settings: InboundWebhookConnectionCreateAuthConfig;
};

export type InboundWebhookConnectionCreateAuthConfig =
  | InboundWebhookConnectionCreateAuthNoAuth
  | InboundWebhookConnectionCreateAuthAPIKey
  | InboundWebhookConnectionCreateAuthBasicAuth
  | InboundWebhookConnectionCreateAuthHMAC
  | InboundWebhookConnectionCreateAuthHMACPersona
  | InboundWebhookConnectionCreateAuthHMACArva
  | InboundWebhookConnectionCreateAuthHMACSyntage
  | InboundWebhookConnectionCreateAuthTruliooHMACHandshake;

export type InboundWebhookConnectionCreateAuthNoAuth = {
  auth_method: InboundWebhookConnectionAuthMethod;
};

export type InboundWebhookConnectionCreateAuthBasicAuth = {
  auth_method: InboundWebhookConnectionAuthMethod;
};

export type InboundWebhookConnectionCreateAuthAPIKey = {
  auth_method: InboundWebhookConnectionAuthMethod;
  header?: string;
  prefix?: string;
};

export type InboundWebhookConnectionCreateAuthHMAC = {
  auth_method: InboundWebhookConnectionAuthMethod;
  hash_algorithm: InboundWebhookHMACHashAlgorithm;
  signature_encoding: InboundWebhookHMACSignatureEncoding;
  header: string;
};

export type InboundWebhookConnectionCreateAuthHMACPersona = {
  auth_method: InboundWebhookConnectionAuthMethod;
};

export type InboundWebhookConnectionCreateAuthHMACArva = {
  auth_method: InboundWebhookConnectionAuthMethod;
};

export type InboundWebhookConnectionCreateAuthHMACSyntage = {
  auth_method: InboundWebhookConnectionAuthMethod;
};

export type InboundWebhookConnectionCreateAuthTruliooHMACHandshake = {
  auth_method: InboundWebhookConnectionAuthMethod;
};

export const DatabaseProviders = [
  "postgresql",
  "cockroachdb",
  "bigquery",
  "snowflake",
  "redshift",
  "mysql",
  "mongodb",
] as const;
export const GenericProviders = ["custom", "webhook", "retool"] as const;

export const IntegrationProviders = [
  "schufa",
  "bkr",
  "boniversum",
  "creditreform",
  "crs",
  "codat",
  "data_x",
  "dnb",
  "crif_b2b",
  "crif_b2c",
  "crif_high_mark",
  "equifax_canada",
  "equifax_uk",
  "equifax_us",
  "equifax_us_commercial_risk",
  "experian_colombia",
  "experian_germany",
  "experian_us_b2b",
  "experian_us",
  "experian_us_clarity_services",
  "socure",
  "plaid",
  "sardine",
  "sentilink",
  "thomson_reuters",
  "creditsafe",
  "trans_union",
] as const;

export const Providers = [
  ...IntegrationProviders,
  ...DatabaseProviders,
  ...GenericProviders,
] as const;

export type DatabaseProvider = (typeof DatabaseProviders)[number];
export type GenericProvider = (typeof GenericProviders)[number];
export type LegacyIntegrationProvider = (typeof IntegrationProviders)[number];
export type IntegrationProvider = LegacyIntegrationProvider | string;

export type Provider = (typeof Providers)[number];

export const NotTestableProviders: (Provider | string)[] = [
  "boniversum",
  "data_x",
  "sardine",
  "sentilink",
  "schufa",
  "thomson_reuters",
  "trans_union",
  "experian_germany",
  "experian_us_clarity_services",
  "equifax_us_commercial_risk",
  "transunion_uk_callreport",
  "anthropic",
  "creditsafe_b2b",
  "creditsafe_b2c",
  "dnb_v2",
  "dnb_sftp",
  "equifax_paynet",
  "equifax_uk_open_banking_insights",
  "experian_b2b_precise_id",
  "experian_us_b2b_v2",
  "experian_us_b2c",
  "loan_pro",
  "mastercard_match_pro",
  "neuro_id",
  "openai",
  "postman_echo",
  "sardine",
  "serasa",
  "slack",
  "socure_v2",
  "transunion_uk",
  "transunion_uk_callvalidate",
  "transunion_uk_cosmos",
] as const;

export const RawRequestsProviders: (Provider | string)[] = [
  "experian_us",
  "trans_union",
  "socure",
  "codat",
  "data_x",
  "experian_us_clarity_services",
  "creditsafe",
  "experian_colombia",
  "equifax_us",
] as const;

// Providers that have background credential-refresh logic in the backend
// (the connect module's `CREDENTIAL_REFRESHERS` registry). The "Disable
// credential refresh" Power Tools toggle is only shown for these; for any
// other provider it is inert, so it is hidden. Keep in sync with the backend
// registry when refreshers are added or removed.
export const CredentialRefreshProviders: (Provider | string)[] = [
  "equifax_uk",
  "equifax_uk_open_banking_insights",
  "buro_de_credito",
  "transunion_uk",
  "transunion_uk_cosmos",
  "transunion_uk_callreport",
  "transunion_uk_callreport_bsb",
] as const;

export const isDatabaseProvider = (
  provider: Provider | string,
): provider is DatabaseProvider =>
  DatabaseProviders.includes(provider as DatabaseProvider);

export const isGenericProvider = (
  provider: Provider | string,
): provider is GenericProvider =>
  GenericProviders.includes(provider as GenericProvider);

export const isIntegrationProvider = (
  provider: Provider | string,
): provider is LegacyIntegrationProvider =>
  !isDatabaseProvider(provider) &&
  !isGenericProvider(provider) &&
  Providers.includes(provider as Provider);

export const SchufaResources = [
  "b2c_report",
  "schufa_b2c_monitor",
  "b2b_report_cascading",
  "schufa_fraud_pre_check",
  "schufa_b2c_furnish",
  "schufa_b2c_basic_account_and_exemption_from_execution_account_report",
  "schufa_b2c_manual_retrieval",
  "schufa_b2c_update_address",
] as const;
export type SchufaResource = (typeof SchufaResources)[number];

const CreditReformResources = [
  "compact_report",
  "traffic_light_report",
  "risk_check_report",
  "brief_report",
  "commercial_report",
  "identification_report",
  "ecrefo_report",
  "search_request",
  "creditreform_mailbox_directory",
  "creditreform_mailbox_entry",
  "commercial_report_without_report",
] as const;

const BKRResources = ["bkr_cki"] as const;

const BoniversumResources = ["consumer_report"] as const;

const CustomResources = ["http", "xml"] as const;
export type CustomResource = (typeof CustomResources)[number];

const InboundWebhookResources = ["event"] as const;
export type InboundWebhookResource = (typeof InboundWebhookResources)[number];

const RetoolResources = ["retool_embed"] as const;
export type RetoolResource = (typeof RetoolResources)[number];

// The same single resource is used for all database connections
const DatabaseConnectionResources = ["database"] as const;
export type DatabaseConnectionResource =
  (typeof DatabaseConnectionResources)[number];

export const CRSResources = [
  "experian_credit_report",
  "transunion_credit_report",
  "transunion_imv_basic",
  "experian_business_profile_report",
  "experian_business_search",
  "experian_intelliscore_business_report",
  "lexisnexis_bridger_search",
  "crs_equifax_credit_report",
] as const;
export type CRSResource = (typeof CRSResources)[number];

export const CodatResources = [
  "company_info",
  "company_identification",
  "enhanced_profit_and_loss_accounts",
  "enhanced_balance_sheet_accounts",
  "aged_creditors_report",
  "aged_debtors_report",
] as const;

const DnbResources = [
  "identity_resolution",
  "credit_report",
  "custom_report",
] as const;

const CRIFB2BResources = [
  "full_report",
  "credit_report",
  "search_report",
] as const;

const CRIFB2CResources = ["identity_check", "credit_check"] as const;

const DataXResources = ["data_x_credit_report"] as const;

const EquifaxCanadaResources = ["equifax_canada_consumer_credit_file"] as const;

const EquifaxUKResources = ["gateway"] as const;

const EquifaxUSResources = [
  "one_view_report",
  "scores_and_attributes",
  "mla_report",
] as const;

const EquifaxUSCommercialRiskResources = [
  "equifax_us_commercial_risk_credit_report",
] as const;

const ExperianColombiaResources = [
  "experian_colombia_credit_history_for_legal_entities",
  "experian_colombia_chamber_of_commerce",
];

const ExperianGermanyResources = [
  "experian_germany_business_information_gateway",
];

const ExperianUSB2BResources = [
  "experian_us_b2b_business_search",
  "experian_us_b2b_small_business_credit_share_scores",
  "experian_us_b2b_small_business_credit_share_profiles_report",
] as const;

const ExperianUSClarityServicesResources = [
  "experian_us_clarity_services_report",
] as const;

const ExperianUSResources = ["experian_us_credit_report"] as const;

const SocureResources = [
  // New bundles
  "socure_fraud_bundle_1",
  "socure_fraud_bundle_2",
  "socure_fraud_bundle_3",
  "socure_fraud_bundle_4",
  "socure_fraud_bundle_5",
  "socure_doc_v_plus_bundle",
  "socure_digital_intelligence",
  // Deprecated bundles
  "compliance_bundle",
  "standard_bundle",
  "premium_bundle",
] as const;

export type SocureResource = (typeof SocureResources)[number];

const CRIFHighMarkResources = ["credit_report"] as const;

const PlaidResources = [
  "asset_report",
  "financial_insights_report",
  "plaid_cra_base_report",
  "plaid_cra_income_insights_report",
  "plaid_cra_partner_insights_report",
] as const;

const SardineResources = [
  "sardine_identity_verification",
  "sardine_feedback",
] as const;

const SentiLinkResources = [
  "sentilink_synthetic_fraud_score",
  "sentilink_identity_theft_score",
  "sentilink_custom_score_products",
  "sentilink_facets",
  "sentilink_insights_report",
  "sentilink_id_complete",
] as const;

const ThomsonReutersResources = [
  "thomson_reuters_clear_person_search",
  "thomson_reuters_clear_business_search",
  "thomson_reuters_risk_person_search",
  "thomson_reuters_risk_business_search",
  "thomson_reuters_person_report",
  "thomson_reuters_business_report",
] as const;

const CreditsafeResources = [
  "company_search",
  "company_credit_report",
] as const;

const TransUnionResources = [
  "tru_vision_model_report",
  "tru_vision_credit_report",
] as const;

export type TransUnionResource = (typeof TransUnionResources)[number];

const ManualReviewResources = ["review_event"] as const;
export type ManualReviewResource = (typeof ManualReviewResources)[number];

const CaseCreationResources = ["case_event"] as const;
export type CaseCreationResource = (typeof CaseCreationResources)[number];

const CaseUpdateResources = ["case_update_event"] as const;
export type CaseUpdateResource = (typeof CaseUpdateResources)[number];

export const IntegrationProviderResourceMap = {
  schufa: SchufaResources,
  bkr: BKRResources,
  boniversum: BoniversumResources,
  creditreform: CreditReformResources,
  crs: CRSResources,
  codat: CodatResources,
  dnb: DnbResources,
  crif_b2b: CRIFB2BResources,
  crif_b2c: CRIFB2CResources,
  crif_high_mark: CRIFHighMarkResources,
  data_x: DataXResources,
  equifax_canada: EquifaxCanadaResources,
  equifax_uk: EquifaxUKResources,
  equifax_us: EquifaxUSResources,
  equifax_us_commercial_risk: EquifaxUSCommercialRiskResources,
  experian_colombia: ExperianColombiaResources,
  experian_germany: ExperianGermanyResources,
  experian_us_b2b: ExperianUSB2BResources,
  experian_us: ExperianUSResources,
  experian_us_clarity_services: ExperianUSClarityServicesResources,
  socure: SocureResources,
  plaid: PlaidResources,
  sardine: SardineResources,
  sentilink: SentiLinkResources,
  thomson_reuters: ThomsonReutersResources,
  creditsafe: CreditsafeResources,
  trans_union: TransUnionResources,
  postgresql: DatabaseConnectionResources,
  cockroachdb: DatabaseConnectionResources,
  bigquery: DatabaseConnectionResources,
  snowflake: DatabaseConnectionResources,
  redshift: DatabaseConnectionResources,
  mongodb: DatabaseConnectionResources,
  retool: RetoolResources,
} as const;

export type IntegrationProviderResourceMapType =
  typeof IntegrationProviderResourceMap;

type IntegrationProviderResourceMapToTypeTransform<T> =
  T extends IntegrationProvider
    ? { provider: T; resource: ResourceTypesForIntegrationProvider<T> }
    : never;

export type IntegrationProviderResource =
  IntegrationProviderResourceMapToTypeTransform<
    keyof IntegrationProviderResourceMapType
  >;

export type ManifestIntegrationProviderResource = {
  provider: string;
  resource: string;
  manifest_version: string;
};

export type ResourceTypesForIntegrationProvider<T extends IntegrationProvider> =
  T extends keyof IntegrationProviderResourceMapType
    ? IntegrationProviderResourceMapType[T][number]
    : string;

export type CustomProviderResource = {
  provider: "custom";
  resource: CustomResource;
};

export type InboundWebhookProviderResource = {
  provider: "webhook";
  resource: InboundWebhookResource;
};

export type ManualReviewProviderResource = {
  provider: "webhook";
  resource: ManualReviewResource;
};

export type CaseCreationProviderResource = {
  provider: "webhook";
  resource: CaseCreationResource;
};

export type CaseUpdateProviderResource = {
  provider: "webhook";
  resource: CaseUpdateResource;
};

export type DatabaseProviderResource = {
  provider: DatabaseProvider;
  resource: DatabaseConnectionResource;
};

export type RetoolProviderResource = {
  provider: "retool";
  resource: RetoolResource;
};

export type ProviderResource =
  | IntegrationProviderResource
  | ManifestIntegrationProviderResource
  | CustomProviderResource
  | DatabaseProviderResource
  | InboundWebhookProviderResource
  | CaseCreationProviderResource
  | CaseUpdateProviderResource
  | ManualReviewProviderResource
  | RetoolProviderResource;

export const Resources = [
  ...SchufaResources,
  ...BKRResources,
  ...BoniversumResources,
  ...CreditReformResources,
  ...CustomResources,
  ...CRSResources,
  ...CodatResources,
  ...DataXResources,
  ...DnbResources,
  ...CRIFB2BResources,
  ...CRIFB2CResources,
  ...CRIFHighMarkResources,
  ...EquifaxCanadaResources,
  ...EquifaxUKResources,
  ...EquifaxUSResources,
  ...EquifaxUSCommercialRiskResources,
  ...ExperianColombiaResources,
  ...ExperianGermanyResources,
  ...ExperianUSB2BResources,
  ...ExperianUSResources,
  ...ExperianUSClarityServicesResources,
  ...SocureResources,
  ...PlaidResources,
  ...SardineResources,
  ...SentiLinkResources,
  ...ThomsonReutersResources,
  ...CreditsafeResources,
  ...TransUnionResources,
  ...InboundWebhookResources,
  ...ManualReviewResources,
  ...RetoolResources,
  ...DatabaseConnectionResources,
  ...CaseCreationResources,
  ...CaseUpdateResources,
  "probe",
] as const;

export type Resource = (typeof Resources)[number];

// We need to support an "arbitrary" nested configuration for
// resources, however we can't simply define recursive types
// b/c then the type becomes "infinite" and TypeScript complains about it.
// The solution was to use interfaces b/c of how TS type resolution works:
// https://github.com/microsoft/TypeScript/issues/3496#issuecomment-128553540
type PrimitiveConfigValue = string | number | boolean | null;

interface ConfigRecordT {
  [key: string]: ConfigurationValue;
}

// This is a workaround to avoid the infinite type error
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ConfigArray extends Array<ConfigurationValue> {}

export type ConfigurationValue =
  | PrimitiveConfigValue
  | ConfigRecordT
  | ConfigArray
  | undefined;

export type Configuration = {
  [key: string]: ConfigurationValue | Record<string, ConfigurationValue>;
};

export type ResourceConfig = {
  id: string;
  name: string;
  resource: Resource;
  configuration: Configuration;
  has_raw_response_enabled: boolean;
  has_raw_requests_enabled_in_resource?: boolean;
  disable_sensitive_data_stripping?: boolean;
  created_at: string;
  updated_at: string;
};

export type BaseAuthSettings = { ssl_enabled: boolean };
export type NoAuthSettings = BaseAuthSettings & { auth_method: "no_auth" };
export type BasicAuthSettings = BaseAuthSettings & { auth_method: "basic" };
export type APIKeyAuthSettings = BaseAuthSettings & {
  auth_method: "api_key";
  header?: string;
  prefix?: string;
  api_key_as_query_param?: boolean;
  query_param_name?: string;
};
export type OAuth2AuthSettings = BaseAuthSettings & {
  auth_method: "oauth2";
  header?: string;
  prefix?: string;
  grant_type?: string;
  uses_basic_auth: boolean;
  basic_auth_header?: string;
  basic_auth_prefix?: string;
  authorize_url: string;
  authorize_query_params?: { [key: string]: string };
  authorize_headers?: { [key: string]: string };
  authorize_body?: object;
  authorize_body_token_path?: string;
  authorize_body_expires_path?: string;
  refresh_url: string;
  refresh_query_params?: { [key: string]: string };
  refresh_headers?: { [key: string]: string };
  refresh_body?: object;
  scopes?: Array<string>;
  scope_delimiter?: string;
  token_refresh_interval?: number;
};
export type AWSSignatureAuthSettings = BaseAuthSettings & {
  auth_method: "aws_signature";
  aws_access_key: string;
  aws_host: string;
  aws_region: string;
  aws_service: string;
};

export type GoogleOAuth2AuthSettings = BaseAuthSettings & {
  auth_method: "google_oauth2";
  authorize_url: string;
  token_refresh_interval?: number;
  google_auth_filename?: string;
  scopes?: Array<string>;
};

export type CustomCodeAuthSettings = BaseAuthSettings & {
  auth_method: "custom_code";
  auth_code: string;
};

export type HMACAuthSettings = BaseAuthSettings & {
  auth_method: "hmac";
  hash_algorithm: HMACHashAlgorithm;
  signature_encoding: HMACSignatureEncoding;
  signature_prefix?: string | null;
  signature_location?: HMACSignatureLocation;
  signature_field_name?: string | null;
};

export type AuthSettings =
  | NoAuthSettings
  | BasicAuthSettings
  | APIKeyAuthSettings
  | OAuth2AuthSettings
  | AWSSignatureAuthSettings
  | GoogleOAuth2AuthSettings
  | CustomCodeAuthSettings
  | HMACAuthSettings;

export type InboundWebhookNoAuthSettings = { auth_method: "no_auth" };
export type InboundWebhookBasicAuthSettings = { auth_method: "basic" };
export type InboundWebhookAPIKeyAuthSettings = {
  auth_method: "api_key";
  header?: string;
  prefix?: string;
};
export type InboundWebhookHMACAuthSettings = {
  auth_method: "hmac";
  hash_algorithm: string;
  signature_encoding: string;
  header: string;
};
export type InboundWebhookHMACPersonaAuthSettings = {
  auth_method: "hmac_persona";
};

export type InboundWebhookHMACArvaAuthSettings = {
  auth_method: "hmac_arva";
};

export type InboundWebhookHMACSyntageAuthSettings = {
  auth_method: "hmac_syntage";
};

export type InboundWebhookTruliooHMACHandshakeAuthSettings = {
  auth_method: "hmac_trulioo_handshake";
};

export type InboundWebhookAuthSettings =
  | InboundWebhookNoAuthSettings
  | InboundWebhookBasicAuthSettings
  | InboundWebhookAPIKeyAuthSettings
  | InboundWebhookHMACAuthSettings
  | InboundWebhookHMACPersonaAuthSettings
  | InboundWebhookHMACArvaAuthSettings
  | InboundWebhookHMACSyntageAuthSettings
  | InboundWebhookTruliooHMACHandshakeAuthSettings;

export type CustomConnectionConfiguration = {
  base_url?: string;
  probe_url?: string;
  media_key?: string;
  headers?: Record<string, string>;
  auth_settings?: AuthSettings;
  allow_test_invocations?: boolean;
};

export type T1ConnectionConfiguration = {
  multi_tenant?: boolean;
};

type InboundWebhookConnectionConfiguration = {
  media_key?: string;
  webhook_auth_settings: InboundWebhookAuthSettings;
  webhook_url?: string | null;
  custom_success_status_code?: number | null;
};

export type PostgreSQLConnectionConfigurationBE = {
  database_name: string;
  host: string;
  port?: number;
  auth_method: PostgreSQLConnectionAuthMethod;
  has_mtls_enabled: boolean;
  mutual_tls?: MutualTLSConfig;
  has_ssh_tunnel_enabled: boolean;
  ssh_tunnel?: SSHTunnelConfig;
};

export type MySQLConnectionConfigurationBE = {
  database_name: string;
  host: string;
  port?: number;
  auth_method: MySQLConnectionAuthMethod;
  has_ssh_tunnel_enabled?: boolean;
  ssh_tunnel?: {
    host: string;
    port: number;
    user: string;
  };
  has_mtls_enabled: boolean;
  mutual_tls?: MutualTLSConfig;
};

export type BigQuerySQLConnectionConfigurationBE = {
  scopes: string[];
  auth_method: BigQuerySQLConnectionAuthMethod;
};

export type SnowflakeConnectionConfigurationBE = {
  account_identifier: string;
  warehouse_name: string;
  database_name: string;
  database_schema?: string;
  user_role?: string;
  auth_method: SnowflakeConnectionAuthMethod;
};

export type MongoDBConnectionConfigurationBE = {
  database_name: string;
  host?: string;
  auth_method: MongoDBConnectionAuthMethod;
  non_srv_host_string?: string;
  auth_source?: string;
};

export type CockroachDBConnectionConfigurationBE = {
  has_retry_enabled: boolean;
  retry_config?: CockroachDBRetryConfig;
} & PostgreSQLConnectionConfigurationBE;

export type RedshiftSSLMode = "verify-ca" | "verify-full";

export type RedshiftConnectionConfigurationBE = {
  host?: string;
  port?: number;
  database_name?: string;
  cluster_identifier?: string;
  aws_region?: string;
  role_arn?: string;
  auth_method: RedshiftConnectionAuthMethod;
  has_ssl_enabled: boolean;
  ssl_mode?: RedshiftSSLMode | null;
  has_ssh_tunnel_enabled: boolean;
  ssh_tunnel?: SSHTunnelConfig;
};

type AuthMethod =
  | PostgreSQLConnectionAuthMethod
  | BigQuerySQLConnectionAuthMethod
  | SnowflakeConnectionAuthMethod
  | RedshiftConnectionAuthMethod
  | MongoDBConnectionAuthMethod;

export type ConnectionConfiguration = {
  allow_test_invocations?: boolean;
  auth_method?: AuthMethod;
} & CustomConnectionConfiguration &
  T1ConnectionConfiguration &
  Partial<InboundWebhookConnectionConfiguration> &
  Partial<Omit<PostgreSQLConnectionConfigurationBE, "auth_method">> &
  Partial<Omit<BigQuerySQLConnectionConfigurationBE, "auth_method">> &
  Partial<Omit<SnowflakeConnectionConfigurationBE, "auth_method">> &
  Partial<Omit<RedshiftConnectionConfigurationBE, "auth_method">> &
  Partial<Omit<MongoDBConnectionConfigurationBE, "auth_method">> &
  Partial<Omit<MySQLConnectionConfigurationBE, "auth_method">> &
  Partial<Omit<CockroachDBConnectionConfigurationBE, "auth_method">>;

export type ConnectionSecret = {
  key: string;
  value: string;
  environment?: string | null;
  tenant_id?: string | null;
};

export type Connection = {
  id: string;
  name: string;
  is_sandbox: boolean;
  provider: Provider | string;
  manifest_version?: string;
  environment_configurations?: Record<string, Record<string, unknown>>;
  created_at: string;
  updated_at: string;
  configuration: ConnectionConfiguration;
  resource_configs: ResourceConfig[];
  secrets: ConnectionSecret[];
  enable_non_prod_configs: boolean;
  has_raw_response_enabled: boolean;
  has_raw_requests_enabled_in_resource: boolean;
  non_prod_env_configs?: Record<string, ConnectionConfiguration>;
  data_retention?: DataRetention;
  disable_credential_bg_jobs?: boolean;
  credentials_refreshed_at?: string | null;
};

export type ResourceSample = {
  sample:
    | {
        data: Record<string, unknown>;
        insights: Record<string, unknown>;
      }
    | GenericObject;
  name: string;
  description?: string;
};

export type MissingEntitySample = {
  name: string;
  sample: null;
  description?: string;
};

export type RetoolEmbedQueryParams = {
  provider_args: { max_age_seconds: number };
  query: { user_id: string | undefined; email: string | undefined };
  resource_config_id: string | undefined;
};

export type RetoolEmbedResponse = {
  data: {
    embed_url: string;
  };
};

export type MockTemplateErrorType =
  | "syntax"
  | "undefined_variable"
  | "render"
  | "validation"
  | "security";

export type MockTemplateError = {
  type: MockTemplateErrorType;
  message: string;
  details: string | null;
  suggestion: string | null;
};

export type MockTemplateCaseResult = {
  case_number: number;
  success: boolean;
  rendered_template: unknown | null;
  rendered_template_raw: string | null;
  mock_data: Record<string, unknown>;
  error: MockTemplateError | null;
};

export type MockTemplateRequest = {
  template: string;
  input_schema: Record<string, unknown>;
  body_type: "json" | "xml" | "raw";
  number_of_cases?: number;
  seed?: number;
  connection_id?: string;
};

export type MockTemplateResponse = {
  results: MockTemplateCaseResult[];
  total_cases: number;
  successful_cases: number;
  failed_cases: number;
  seed: number;
};

export type SampleRequestConfig = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  url?: string;
  headers?: Record<string, string>;
  body?: string;
};

export type ValidateCustomAuthRequest = {
  auth_code: string;
  secrets?: Record<string, string>;
  config?: Record<string, unknown>;
  sample_request?: SampleRequestConfig;
};

export type ValidateCustomAuthError = {
  type: string;
  message: string;
};

export type ValidateCustomAuthRawRequest = {
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string | null;
};

export type ValidateCustomAuthResponse = {
  success: boolean;
  error?: ValidateCustomAuthError | null;
  raw_request?: ValidateCustomAuthRawRequest | null;
};
